// What every page shares: the workspace's connection, who the person is as
// the carrier names them, what their role is offered, and what the workspace
// is called on the page — its display name from the profile the shell read,
// which Settings may change while the console is open.

import { createContext, useContext, type ReactNode } from "react";

import type { Bus, Who } from "../platform/bus.ts";
import { workspaceName } from "../platform/names.ts";
import { useConnection } from "../platform/session.tsx";
import { manages, offered, type Role, type Verb } from "../roles/offers.ts";

/** Workspace is what the shell knows of the workspace beyond the carrier: its name, and how to change it. */
export interface Workspace {
  /** The display name as a page shows it: the profile's, or the slug where the profile carries none. */
  name: string;
  /** rename is what Settings calls once the service has the new name. */
  rename: (name: string) => void;
}

const WorkspaceContext = createContext<Workspace | undefined>(undefined);

export function WorkspaceProvider({ value, children }: { value: Workspace; children: ReactNode }) {
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export interface Surface {
  bus: Bus;
  who: Who;
  /** The person's stable subject, as the carrier names it. */
  me: string;
  /**
   * The person's principal token, as the carrier names it: the one subject
   * token they are where one is needed — their own vault namespace
   * (02-DESIGN/08-toolkits/03-linking.md § the namespace). Empty on a sign-in
   * service that does not stamp it yet.
   */
  principal: string;
  role: Role | undefined;
  /** The tenant's slug, as the carrier names it. */
  tenant: string;
  /** The workspace's name, as a page shows it. */
  name: string;
  /** rename tells the shell the name changed, so the rail and every page follow at once. */
  rename: (name: string) => void;
  offered: (v: Verb) => boolean;
  manages: (registrant: string) => boolean;
}

/** useSurface is the context every page draws from; a page renders only on a connected console. */
export function useSurface(): Surface {
  const { bus, state } = useConnection();
  const who = state.kind === "connected" ? state.who : bus.who();
  if (who === undefined) {
    throw new Error("a page outside a connected console");
  }
  const workspace = useContext(WorkspaceContext);
  const role = who.role;
  const me = who.claims?.sub ?? "";
  const principal = who.claims?.principal ?? "";
  const tenant = who.claims?.tenant ?? "";
  return {
    bus,
    who,
    me,
    principal,
    role,
    tenant,
    name: workspace?.name ?? workspaceName(tenant),
    rename: workspace?.rename ?? (() => undefined),
    offered: (v) => offered(role, v),
    manages: (registrant) => manages(role, me, registrant),
  };
}
